import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { PrintSubject } from './subjectGroupTypes';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';

// ─────────────────────────────────────────────────────────────────────────────
// SubjectExcluderModal — hide subjects from printouts only (marks are never
// deleted, this only affects what appears on the class result / report card)
// ─────────────────────────────────────────────────────────────────────────────

interface SubjectExcluderModalProps {
  visible: boolean;
  onHide: () => void;
  subjects: PrintSubject[];
  excludedIds: number[];
  onChange: (ids: number[]) => void;
}

const SubjectExcluderModal: React.FC<SubjectExcluderModalProps> = ({
  visible,
  onHide,
  subjects,
  excludedIds,
  onChange,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const excludedSet = new Set(excludedIds);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.excluder-modal-wrapper .p-dialog') as HTMLElement | null;
      const mask   = document.querySelector('.excluder-modal-wrapper')           as HTMLElement | null;
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask)   mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const toggleSubject = (id: number) => {
    const next = new Set(excludedSet);
    next.has(id) ? next.delete(id) : next.add(id);
    onChange(Array.from(next));
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => { onHide(); setIsClosing(false); }, 380);
  };

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '4px 0' }}>
      <div style={{
        width: 48, height: 48, borderRadius: 13,
        background: 'linear-gradient(135deg,#92400e,#f59e0b)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, boxShadow: '0 6px 18px rgba(245,158,11,.25)',
        border: '2.5px solid white',
      }}>
        <i className="pi pi-eye-slash" style={{ fontSize: 20, color: 'white' }} />
      </div>
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#92400e' }}>
          Exclude Subjects
        </div>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
          Hidden from printouts only — marks stay untouched
        </div>
      </div>
    </div>
  );

  return (
    <Dialog
      visible={visible && !isClosing}
      onHide={handleClose}
      header={headerTemplate}
      style={{ width: '94vw', maxWidth: 480, borderRadius: 22, overflow: 'hidden' }}
      contentStyle={{
        padding: '18px 24px 8px',
        background: 'linear-gradient(180deg,#f8fafc,#f1f5f9)',
      }}
      headerStyle={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '18px 24px',
        borderRadius: '22px 22px 0 0',
      }}
      modal
      draggable={false}
      resizable={false}
      blockScroll
      dismissableMask
      maskClassName="excluder-modal-wrapper"
    >
      <style>{`
        .excluder-modal-wrapper { backdrop-filter:blur(4px); background-color:rgba(0,0,0,.22)!important; animation:emFadeIn .3s ease; }
        .excluder-modal-wrapper.mask-closing { animation:emFadeOut .38s ease!important; }
        @keyframes emFadeIn { from{opacity:0} to{opacity:1} }
        @keyframes emFadeOut { from{opacity:1} to{opacity:0} }
        .excluder-modal-wrapper .p-dialog { animation:emSlideIn .3s cubic-bezier(.4,0,.2,1); box-shadow:0 20px 48px -8px rgba(0,0,0,.22)!important; }
        .excluder-modal-wrapper .p-dialog.dialog-closing { animation:emSlideOut .38s cubic-bezier(.4,0,.2,1)!important; }
        @keyframes emSlideIn { from{opacity:0;transform:scale(.93) translateY(-16px)} to{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes emSlideOut { from{opacity:1;transform:scale(1) translateY(0)} to{opacity:0;transform:scale(.9) translateY(18px)} }
        .subject-toggle-chip { display:flex; align-items:center; gap:10px; cursor:pointer; border-radius:11px; padding:10px 13px; margin-bottom:8px; border:1.5px solid #e5e7eb; background:white; transition:all .15s; }
        .subject-toggle-chip:hover { border-color:#bfdbfe; }
        .subject-toggle-chip.excluded { background:#f9fafb; border-color:#e5e7eb; }
      `}</style>

      <div style={{
        display: 'flex', gap: 8, background: '#fffbeb', border: '1px solid #fde68a',
        borderRadius: 10, padding: '10px 12px', marginBottom: 14, fontSize: 12, color: '#92400e', fontWeight: 600,
      }}>
        <i className="pi pi-info-circle" style={{ marginTop: 1 }} />
        Excluded subjects are hidden from printouts only. Marks are never deleted.
      </div>

      {excludedIds.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <GlobalButton label="Clear all" variant="outline" size="small" rounded={false} onClick={() => onChange([])} />
        </div>
      )}

      {subjects.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px 0', color: '#64748b', fontSize: 13 }}>No subjects available</div>
      ) : (
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {subjects.map((sub) => {
            const isExcluded = excludedSet.has(sub.subject_id);
            return (
              <div
                key={sub.subject_id}
                className={`subject-toggle-chip${isExcluded ? ' excluded' : ''}`}
                onClick={() => toggleSubject(sub.subject_id)}
              >
                <i className={isExcluded ? 'pi pi-eye-slash' : 'pi pi-eye'} style={{ color: isExcluded ? '#9ca3af' : '#2855AE', fontSize: 15 }} />
                <span style={{
                  fontSize: 13, fontWeight: 700, flex: 1,
                  color: isExcluded ? '#9ca3af' : '#111827',
                  textDecoration: isExcluded ? 'line-through' : 'none',
                }}>
                  {sub.subject_name}
                </span>
                <span style={{
                  fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 10,
                  background: isExcluded ? '#f3f4f6' : '#f0fdf4',
                  color: isExcluded ? '#9ca3af' : '#0f766e',
                  border: `1px solid ${isExcluded ? '#d1d5db' : '#bbf7d0'}`,
                }}>
                  {isExcluded ? 'EXCLUDED' : 'INCLUDED'}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 18, marginBottom: 6 }}>
        <GlobalButton label="Done" icon="pi pi-check" fullWidth onClick={handleClose} />
      </div>
    </Dialog>
  );
};

export default SubjectExcluderModal;